import { useEffect, useState } from 'react';
import { Card } from './ui/Card';
import {
  getCustomerFeatures,
  setCustomerFeature,
  PORTAL_FEATURE,
} from '../api/features';

/**
 * Staff toggle: gate the client's portal Email Forwarding self-service tool.
 * Off by default (opt-in). The server also enforces the flag, so this controls
 * both visibility and access, not just the UI.
 */
export function EmailForwardingToggle({ customerId }: { customerId: string }) {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    getCustomerFeatures(customerId)
      .then((res) => {
        if (active) setEnabled(!!res.features[PORTAL_FEATURE.EMAIL_FORWARDING]);
      })
      .catch((err) => {
        if (active) setError(err instanceof Error ? err.message : 'Failed to load setting');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [customerId]);

  const toggle = async () => {
    const next = !enabled;
    setSaving(true);
    setError(null);
    try {
      await setCustomerFeature(customerId, PORTAL_FEATURE.EMAIL_FORWARDING, next);
      setEnabled(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save setting');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">
            Email forwarding self-service
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            When on, this client can create forwarding addresses (e.g.{' '}
            <span className="font-mono">info@theirdomain.com</span>) from their portal.
            Off by default.
          </p>
          {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          aria-label="Toggle email forwarding self-service"
          disabled={loading || saving}
          onClick={toggle}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
            enabled ? 'bg-[#3AABE8]' : 'bg-gray-300'
          }`}
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
              enabled ? 'translate-x-5' : 'translate-x-0.5'
            }`}
          />
        </button>
      </div>
    </Card>
  );
}
